// Server-verified missions (phase 2: the domains in SERVER_LAB_DOMAINS), real accounts only.
//
// The local progress record keeps working exactly as before for the mission flow itself:
// unlocking, the map, resuming a lab. What counts for REWARDS — XP, levels, domain medals, titles,
// Your week, badges — comes from public.completions, which only the verify-lab Edge Function can
// write after replaying the lab journal (src/core/labVerify.js).
//
//   load()   read this account's verified missions (after sign-in / on boot)
//   check()  send each locally completed, not-yet-verified mission (in path order) for replay
//   view     a read-only progress adapter for reward code: in server-verified domains a mission is
//            complete only when the server has verified it; other domains use the local record
//
// Incident Labs (Phase 3): a solved incident run (lab:inc.<id>) is also sent to verify-lab, after
// the missions. Verified incidents are kept in their OWN map (`incidents`), never in `rows`, so
// they never count as missions, domain completion, medals, quests, Your week or certificates.
// Phase 4: `view.incidentXp()` adds the XP of each verified incident (first verified solve,
// src/core/xp.js incidentXp) to the XP total (src/map/mapState.js operatorStats) — nothing else.
// Local practice works exactly as before whether or not the server is reachable.
import { SERVER_LAB_DOMAINS, SERVER_LAB_KEYS, SERVER_INCIDENT_KEYS, labPayload } from './labVerify.js';
import { INCIDENTS_KEY } from './incidentProgress.js';
import { HINT_XP_RULE, incidentXp } from './xp.js';

/** Rejects when `p` has not settled after `ms` (a hung request is "unavailable", never "verified"). */
const withTimeout = (p, ms) => new Promise((resolve, reject) => {
  const t = setTimeout(() => reject(new Error('timeout')), ms);
  p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
});

/** Server answers that mean "this saved run can't be checked — do the mission again". */
const REDO = new Set(['no-journal', 'journal-too-long', 'outdated', 'not-complete', 'activity-rejected', 'activity-not-done', 'bad-command', 'bad-change', 'bad-activity', 'bad-entry', 'invalid-setup', 'replay-failed', 'setup-failed']);
const keyOf = (d, u) => `${d}.${u}`;
const covered = (d) => SERVER_LAB_DOMAINS.includes(d);

/**
 * @param {{ client: object, store: object, progress: object, curriculum: object, userId: () => (string|null), debounceMs?: number }} o
 *   client: supabase-js client · store: learner store · progress: learner createProgress
 */
export function createVerifiedLabs({ client, store, progress, curriculum, userId, debounceMs = 800, incidentTimeoutMs = 30000 }) {
  let rows = new Map(); // key → { at, hints, xp }   (curriculum missions only)
  let incidents = new Map(); // 'inc.<id>' → { at, hints }   (verified incident runs; no rewards)
  let incidentReasons = {}; // 'inc.<id>' → last refusal; cleared when a new run is solved (store key 'incidents')
  let incidentSending = null; // 'inc.<id>' whose request is in flight
  let incidentDown = new Set(); // 'inc.<id>' whose last request failed / timed out (no answer): retried later
  let reasons = {};
  let loadedFor = null;
  let status = 'idle'; // idle | loading | checking | ok | offline
  let timer = null;
  let running = null;
  const listeners = new Set();
  const emit = (type, extra = {}) => { for (const f of listeners) { try { f({ type, ...extra }); } catch { /* listener errors never break sync */ } } };

  const localDone = (k) => { const [d, u] = k.split('.'); return progress.unitState(d, u) === 'complete' && progress.unitRecord(d, u).validated !== false && !progress.unitRecord(d, u).bypass; };
  /**
   * The finished run to send: the lab record on this device, or its journal copy `labj:<mission>`
   * (kept in the account by src/core/cloudSync.js, so a reload or another device can still send it).
   */
  const runOf = (k) => {
    const lab = store.get(`lab:${k}`);
    if (lab?.completed === true && Array.isArray(lab.journal)) return labPayload(lab);
    const j = store.get(`labj:${k}`);
    return j && Array.isArray(j.journal) ? labPayload(j) : null;
  };
  const hasJournal = (k) => !!runOf(k);
  const pending = () => SERVER_LAB_KEYS.filter((k) => localDone(k) && !rows.has(k));
  /** A solved incident run on this device, ready to send (local practice record; not a bypass run). */
  const incidentRun = (k) => {
    const lab = store.get(`lab:${k}`);
    return lab?.completed === true && Array.isArray(lab.journal) && !lab.journalOverflow ? labPayload(lab) : null;
  };
  const pendingIncidents = () => SERVER_INCIDENT_KEYS.filter((k) => !incidents.has(k) && !!incidentRun(k) && incidentReasons[k] === undefined);

  async function load() {
    const uid = userId();
    if (!uid || !client) return false;
    status = 'loading';
    const { data, error } = await client.from('completions').select('item_id, kind, xp, details, verified_at').eq('user_id', uid);
    if (userId() !== uid) return false; // account changed meanwhile
    if (error) { status = 'offline'; emit('status'); return false; }
    rows = new Map();
    incidents = new Map();
    for (const r of data || []) {
      if (r.kind !== 'lab') continue;
      const row = { at: Date.parse(r.verified_at) || Date.now(), hints: Math.max(0, Number(r.details?.hints) || 0) };
      if (SERVER_LAB_KEYS.includes(r.item_id)) rows.set(r.item_id, row);
      else if (SERVER_INCIDENT_KEYS.includes(r.item_id)) incidents.set(r.item_id, row);
    }
    loadedFor = uid; status = 'ok';
    emit('loaded');
    return true;
  }

  async function check() {
    const uid = userId();
    if (!uid || !client) return null;
    if (loadedFor !== uid && !(await load())) return null;
    if (!pending().length && !pendingIncidents().length) return { newly: [] };
    if (running) return running;
    // NB: the promise is cleared by the caller below, never inside it — when every pending mission
    // is skipped the body finishes synchronously, and an inner `finally` would run before
    // `running` is assigned, leaving a settled promise there forever.
    const p = (async () => {
      status = 'checking'; emit('status');
      const newly = [];
      try {
        reasons = {};
        for (const k of pending()) {
          const lab = runOf(k);
          if (!lab) { reasons[k] = 'no-journal'; continue; }
          if (api.blockerOf(k)) { reasons[k] = 'prereq'; continue; } // an earlier mission is not verified yet: the server would refuse
          const { data, error } = await client.functions.invoke('verify-lab', { body: { mission: k, lab } });
          if (userId() !== uid) return null;
          if (error || !data) { status = 'offline'; emit('status'); return null; }
          if (data.ok) { rows.set(k, { at: Date.now(), hints: Math.max(0, Number(data.hints) || 0) }); newly.push(k); emit('verified', { newly: [k] }); }
          else reasons[k] = String(data.reason || 'not-complete');
        }
        // solved incident runs (no prerequisite, no rewards: kept apart from `rows`)
        // Only an explicit success for THIS key (ok === true, mission === k) marks it verified; an
        // error, a timeout or any other answer never does.
        for (const k of pendingIncidents()) {
          incidentSending = k; incidentDown.delete(k); emit('incident-status', { key: k });
          let res;
          try { res = await withTimeout(client.functions.invoke('verify-lab', { body: { mission: k, lab: incidentRun(k) } }), incidentTimeoutMs); } catch { res = null; }
          incidentSending = null;
          if (userId() !== uid) return null;
          const data = res && !res.error ? res.data : null;
          if (!data || typeof data !== 'object') { incidentDown.add(k); status = 'offline'; emit('incident-status', { key: k }); emit('status'); return null; }
          if (data.ok === true && data.mission === k) { incidents.set(k, { at: Date.now(), hints: Math.max(0, Number(data.hints) || 0) }); emit('incident-verified', { key: k }); }
          else { incidentReasons[k] = String(data.reason || 'not-verified'); emit('incident-status', { key: k }); }
        }
        status = 'ok';
        emit('checked', { newly });
        return { newly };
      } catch { status = 'offline'; emit('status'); return null; }
    })();
    running = p;
    p.finally(() => { if (running === p) running = null; });
    return p;
  }

  const off = store.onChange?.((key) => {
    if ((key !== 'progress' && key !== INCIDENTS_KEY) || !userId()) return;
    if (key === INCIDENTS_KEY) incidentReasons = {}; // a newly solved run may be sent
    clearTimeout(timer); timer = setTimeout(() => { check(); }, debounceMs);
  });

  // ------------------------------------------------------------------ reward view (progress-like)
  const verifiedRecord = (d, u) => {
    const r = rows.get(keyOf(d, u));
    return { ...progress.unitRecord(d, u), status: 'complete', validated: true, test: false, bypass: false, completedAt: r.at, hints: r.hints, xpRule: HINT_XP_RULE };
  };
  const domainOf = (id) => curriculum.domains.find((x) => x.id === id);
  const view = {
    unitState(d, u) {
      const s = progress.unitState(d, u);
      if (!covered(d) || s !== 'complete' || rows.has(keyOf(d, u))) return s;
      return 'available';
    },
    unitRecord(d, u) {
      if (!covered(d)) return progress.unitRecord(d, u);
      if (rows.has(keyOf(d, u))) return verifiedRecord(d, u);
      const { status: st, completedAt, ...rest } = progress.unitRecord(d, u);
      return st === 'complete' ? rest : { ...rest, ...(st ? { status: st } : {}), ...(completedAt ? { completedAt } : {}) };
    },
    isDomainComplete(d) {
      if (!covered(d)) return progress.isDomainComplete(d);
      const units = domainOf(d)?.units || [];
      return units.length > 0 && units.every((u) => u.implemented && rows.has(keyOf(d, u.id)));
    },
    domainSummary(d) {
      const s = progress.domainSummary(d);
      if (!covered(d)) return s;
      const units = domainOf(d).units;
      const done = units.filter((u) => rows.has(keyOf(d, u.id))).length;
      return { ...s, done, complete: view.isDomainComplete(d), pct: units.length ? Math.round((done / units.length) * 100) : 0 };
    },
    isDomainUnlocked: (d) => progress.isDomainUnlocked(d),
    /** XP from verified incidents: one award per incident, for its first verified solve (Phase 4). */
    incidentXp: () => [...incidents].reduce((s, [k, r]) => s + (SERVER_INCIDENT_KEYS.includes(k) ? incidentXp(r.hints) : 0), 0),
    currentUnit: (d) => progress.currentUnit(d),
    canOpen: (d, u) => progress.canOpen(d, u),
    raw() {
      const r = progress.raw();
      for (const d of SERVER_LAB_DOMAINS) {
        for (const u of domainOf(d)?.units || []) {
          const k = keyOf(d, u.id);
          if (rows.has(k)) r.units[k] = verifiedRecord(d, u.id);
          else if (r.units[k]?.status === 'complete') { delete r.units[k].status; delete r.units[k].completedAt; }
        }
        r.domains[d] = { ...(r.domains[d] || {}) };
        if (view.isDomainComplete(d)) r.domains[d].completedAt = Math.max(...[...rows].filter(([k]) => k.startsWith(`${d}.`)).map(([, v]) => v.at));
        else delete r.domains[d].completedAt;
      }
      return r;
    },
  };

  const api = {
    view,
    load, check, pending,
    isVerified: (k) => rows.has(k),
    /** Has the server verified a solved run of this incident ('inc.<id>')? Never counts for rewards. */
    isIncidentVerified: (k) => incidents.has(k),
    /** XP this incident earned (its first verified solve), or 0 when it is not verified. */
    incidentXpOf: (k) => (incidents.has(k) && SERVER_INCIDENT_KEYS.includes(k) ? incidentXp(incidents.get(k).hints) : 0),
    pendingIncidents,
    /**
     * Verification status of an incident for this account:
     *   'verified'    the server replayed a solved run and accepted it (explicit success only)
     *   'checking'    the request is in flight, or this account's verified list is still loading
     *   'pending'     solved locally, waiting to be sent
     *   'failed'      the server checked the run and refused it (incidentReason says why)
     *   'unavailable' no answer from the server (offline, error, timeout) — retried later
     *   'local'       solved locally, nobody signed in to verify it for
     *   null          no solved run on this device (and nothing verified)
     */
    incidentStatusOf(k) {
      if (!SERVER_INCIDENT_KEYS.includes(k)) return null;
      const uid = userId();
      if (uid && loadedFor === uid && incidents.has(k)) return 'verified';
      if (store.get(`lab:${k}`)?.completed !== true) return null;
      if (!uid) return 'local';
      if (!incidentRun(k)) return 'failed'; // solved, but the run cannot be checked (journal too long / missing)
      if (incidentSending === k) return 'checking';
      if (loadedFor !== uid) return status === 'offline' ? 'unavailable' : 'checking';
      if (incidentReasons[k] !== undefined) return 'failed';
      if (incidentDown.has(k)) return 'unavailable';
      return 'pending';
    },
    /** The server's last refusal for this incident's current run, or null. */
    incidentReason: (k) => incidentReasons[k] || (store.get(`lab:${k}`)?.completed === true && !incidentRun(k) ? 'journal-too-long' : null),
    covers: (d) => covered(d),
    /**
     * 'verified' · 'checking' · 'redo' (the server can't check this run — do the mission again)
     * · 'waiting' (checked once an earlier mission is verified) · 'unverified' (could not reach the
     * server) · null (not complete locally, or not a server-verified domain)
     */
    stateOf(k) {
      const [d] = k.split('.');
      if (!covered(d) || !SERVER_LAB_KEYS.includes(k)) return null;
      if (rows.has(k)) return 'verified';
      if (!localDone(k)) return null;
      if (!hasJournal(k) || REDO.has(reasons[k])) return 'redo';
      if (status === 'checking' || status === 'loading' || loadedFor !== userId()) return 'checking';
      if (api.blockerOf(k)) return 'waiting';
      return 'unverified';
    },
    /** The first earlier mission in the same domain that is not verified yet: { key, number } or null. */
    blockerOf(k) {
      const [d, u] = k.split('.');
      const units = domainOf(d)?.units || [];
      const i = units.findIndex((x) => x.id === u);
      for (let j = 0; j < i; j++) if (units[j].implemented && !rows.has(keyOf(d, units[j].id))) return { key: keyOf(d, units[j].id), number: j + 1 };
      return null;
    },
    /** Missions that need a redo (in path order). */
    needsRedo() { return SERVER_LAB_KEYS.filter((k) => api.stateOf(k) === 'redo'); },
    get status() { return status; },
    get loadedFor() { return loadedFor; },
    onChange(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    /** Sign-out / account switch: forget the previous account's verified missions. */
    reset() { clearTimeout(timer); rows = new Map(); incidents = new Map(); incidentReasons = {}; incidentSending = null; incidentDown = new Set(); reasons = {}; loadedFor = null; status = 'idle'; emit('reset'); },
    destroy() { off?.(); clearTimeout(timer); listeners.clear(); },
  };
  return api;
}
